import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMeetings,
  getUsers,
  cancelMeeting,
  deleteMeeting,
  getCalendarBlockers,
  getCalendarBlocker,
} from '../services/api';
import { useAuth } from '../store/AuthContext';
import { useDialog } from '../components/DialogProvider';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  CalendarMonth as CalendarMonthIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import BlockerDialog from '../components/BlockerDialog';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const SHOW_AS_COLORS = {
  Free: '#38a169',         // green
  Tentative: '#dd6b20',    // orange
  Busy: '#805ad5',         // purple
  OutOfOffice: '#e53e3e',  // red
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const dialog = useDialog();
  const [meetings, setMeetings] = useState([]);
  const [blockers, setBlockers] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [viewDate, setViewDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [loading, setLoading] = useState(true);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [blockerDialog, setBlockerDialog] = useState({ open: false, mode: 'create', occurrence: null, blockerData: null });

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await getUsers();
      setUsers(data || []);
    } catch { /* silent */ }
  }, []);

  const showOwnBlockers = !selectedUserId
    || String(selectedUserId) === String(currentUser?.id);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedUserId) params.userId = selectedUserId;
      const from = new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1);
      const to = new Date(viewDate.getFullYear(), viewDate.getMonth() + 2, 0);
      params.from = from.toISOString();
      params.to = to.toISOString();

      const meetingPromise = getMeetings(params);
      const blockerPromise = showOwnBlockers
        ? getCalendarBlockers({ from: from.toISOString(), to: to.toISOString() })
        : Promise.resolve({ data: [] });

      const [m, b] = await Promise.all([meetingPromise, blockerPromise]);
      setMeetings(m.data || []);
      setBlockers(b.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [viewDate, selectedUserId, showOwnBlockers]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCancel = async (id) => {
    if (!(await dialog.confirm({ title: 'Cancel meeting', message: 'Cancel this meeting?', confirmText: 'Cancel meeting' }))) return;
    try {
      await cancelMeeting(id);
      fetchAll();
      setSelectedMeeting(null);
    } catch { /* silent */ }
  };

  const handleDelete = async (id) => {
    if (!(await dialog.confirm({ title: 'Delete meeting', message: 'Delete this meeting permanently?', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteMeeting(id);
      fetchAll();
      setSelectedMeeting(null);
    } catch { /* silent */ }
  };

  const generateIcsUrl = (m) => {
    const fmt = (d) => new Date(d).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const lines = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT',
      `DTSTART:${fmt(m.startTime)}`, `DTEND:${fmt(m.endTime)}`,
      `SUMMARY:${m.title}`, `DESCRIPTION:${(m.description || '').replace(/\n/g, '\\n')}`,
      `LOCATION:${m.location || ''}`,
      ...m.attendees.map(a => `ATTENDEE;CN=${a.fullName}:mailto:${a.email}`),
      'END:VEVENT', 'END:VCALENDAR'
    ];
    return 'data:text/calendar;charset=utf-8,' + encodeURIComponent(lines.join('\r\n'));
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));
  const goToday = () => setViewDate(new Date());

  const itemsForRange = (rangeStart, rangeEnd) => {
    const m = meetings.filter(x => {
      const s = new Date(x.startTime);
      return s >= rangeStart && s < rangeEnd;
    }).map(x => ({ kind: 'meeting', start: x.startTime, end: x.endTime, raw: x }));

    const b = blockers.filter(x => {
      const s = new Date(x.startTime);
      return s >= rangeStart && s < rangeEnd;
    }).map(x => ({ kind: 'blocker', start: x.startTime, end: x.endTime, raw: x }));

    return [...m, ...b].sort((a, b) => new Date(a.start) - new Date(b.start));
  };

  const getItemsForDay = (day) => {
    const dayStart = new Date(year, month, day);
    const dayEnd = new Date(year, month, day + 1);
    return itemsForRange(dayStart, dayEnd);
  };

  const getWeekStart = () => {
    const d = new Date(viewDate);
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const prevWeek = () => setViewDate(new Date(viewDate.getTime() - 7 * 86400000));
  const nextWeek = () => setViewDate(new Date(viewDate.getTime() + 7 * 86400000));

  const weekStart = getWeekStart();
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const getWeekItemsForDay = (date) => {
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    return itemsForRange(dayStart, dayEnd);
  };

  const monthName = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const meetingColor = (m) => {
    if (m.isCancelled) return '#e53e3e';
    const start = new Date(m.startTime);
    if (start < today) return '#a0aec0';
    return '#3182ce';
  };

  const blockerColor = (b) => SHOW_AS_COLORS[b.showAs] || '#805ad5';

  const itemColor = (it) =>
    it.kind === 'meeting' ? meetingColor(it.raw) : blockerColor(it.raw);

  const itemLabel = (it) => {
    const s = new Date(it.start);
    const time = it.raw.isAllDay
      ? 'All day'
      : s.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const prefix = it.kind === 'blocker' ? '[Block] ' : '';
    return `${time} ${prefix}${it.raw.title}`;
  };

  const openCreateBlocker = () => {
    setBlockerDialog({ open: true, mode: 'create', occurrence: null, blockerData: null });
  };

  const openBlockerOccurrence = async (occurrence) => {
    try {
      const { data } = await getCalendarBlocker(occurrence.blockerId);
      setBlockerDialog({ open: true, mode: 'edit-series', occurrence, blockerData: data });
    } catch { /* silent */ }
  };

  const handleItemClick = (it) => {
    if (it.kind === 'meeting') setSelectedMeeting(it.raw);
    else openBlockerOccurrence(it.raw);
  };

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Stack direction="row" sx={{ alignItems: 'center' }} spacing={1}>
          <CalendarMonthIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Calendar</Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<BlockIcon />}
            onClick={openCreateBlocker}
          >
            Block Time
          </Button>
          <TextField
            select
            size="small"
            value={selectedUserId}
            onChange={e => setSelectedUserId(e.target.value)}
            sx={{ width: 200 }}
          >
            <MenuItem value="">All Users</MenuItem>
            {users.filter(u => u.isActive).map(u => (
              <MenuItem key={u.id} value={u.id}>{u.fullName || u.username}</MenuItem>
            ))}
          </TextField>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(_, v) => { if (v) setView(v); }}
            size="small"
          >
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
        <Button variant="outlined" size="small" onClick={view === 'month' ? prevMonth : prevWeek}>
          <ChevronLeftIcon fontSize="small" />
        </Button>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 240, textAlign: 'center' }}>
          {view === 'month' ? monthName : `${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
        </Typography>
        <Button variant="outlined" size="small" onClick={view === 'month' ? nextMonth : nextWeek}>
          <ChevronRightIcon fontSize="small" />
        </Button>
        <Button variant="outlined" size="small" onClick={goToday}>Today</Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : view === 'month' ? (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
            {WEEKDAYS.map(d => (
              <Typography key={d} variant="caption" sx={{ py: 1, textAlign: 'center', fontWeight: 600, color: 'text.secondary' }}>
                {d}
              </Typography>
            ))}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {Array.from({ length: firstDay }, (_, i) => (
              <Box key={`empty-${i}`} sx={{ minHeight: 80, borderRight: '1px solid', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const dayItems = getItemsForDay(day);
              const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
              return (
                <Box key={day} sx={{
                  minHeight: 80, p: 0.5, borderRight: '1px solid', borderBottom: '1px solid', borderColor: 'divider',
                  bgcolor: isToday ? 'primary.50' : 'background.paper',
                }}>
                  <Box sx={{
                    fontSize: '0.8rem', fontWeight: isToday ? 700 : 400,
                    width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: isToday ? 'primary.main' : 'transparent', color: isToday ? '#fff' : 'text.secondary',
                    mb: 0.25,
                  }}>
                    {day}
                  </Box>
                  {dayItems.slice(0, 3).map((it, idx) => {
                    const c = itemColor(it);
                    const isBlocker = it.kind === 'blocker';
                    return (
                      <Box
                        key={`${it.kind}-${it.raw.id || it.raw.blockerId}-${idx}`}
                        onClick={() => handleItemClick(it)}
                        sx={{
                          fontSize: '0.7rem', px: 0.5, py: '1px', mb: '1px', borderRadius: '3px',
                          bgcolor: c + (isBlocker ? '15' : '20'),
                          color: c,
                          border: isBlocker ? `1px dashed ${c}` : 'none',
                          cursor: 'pointer', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                        }}
                      >
                        {itemLabel(it)}
                      </Box>
                    );
                  })}
                  {dayItems.length > 3 && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', pl: 0.5, fontSize: '0.65rem' }}>
                      +{dayItems.length - 3} more
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {weekDays.map((date, i) => {
              const dayItems = getWeekItemsForDay(date);
              const isToday = date.getTime() === today.getTime();
              return (
                <Box key={i} sx={{
                  minHeight: 200, borderRight: i < 6 ? '1px solid' : 'none', borderColor: 'divider',
                  bgcolor: isToday ? 'primary.50' : 'background.paper',
                }}>
                  <Box sx={{
                    py: 1, px: 0.75, borderBottom: '1px solid', borderColor: 'divider', textAlign: 'center',
                    bgcolor: isToday ? 'primary.main' : 'grey.50', color: isToday ? '#fff' : 'text.secondary',
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{WEEKDAYS[i]}</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>{date.getDate()}</Typography>
                  </Box>
                  <Box sx={{ p: 0.5 }}>
                    {dayItems.map((it, idx) => {
                      const c = itemColor(it);
                      const isBlocker = it.kind === 'blocker';
                      return (
                        <Box
                          key={`${it.kind}-${it.raw.id || it.raw.blockerId}-${idx}`}
                          onClick={() => handleItemClick(it)}
                          sx={{
                            p: '4px 6px', mb: 0.5, borderRadius: 1, cursor: 'pointer',
                            bgcolor: c + '15',
                            borderLeft: `3px ${isBlocker ? 'dashed' : 'solid'} ${c}`,
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600, color: c, display: 'block' }}>
                            {it.raw.isAllDay
                              ? 'All day'
                              : new Date(it.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.primary', display: 'block' }}>
                            {isBlocker ? `[${it.raw.showAs}] ` : ''}{it.raw.title}
                          </Typography>
                          {it.kind === 'meeting' && it.raw.leadName && (
                            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', fontSize: '0.65rem' }}>
                              {it.raw.leadName}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                    {dayItems.length === 0 && (
                      <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: 'center', py: 1 }}>
                        No events
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      <Dialog
        open={!!selectedMeeting}
        onClose={() => setSelectedMeeting(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedMeeting && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography variant="h6">{selectedMeeting.title}</Typography>
              <IconButton size="small" onClick={() => setSelectedMeeting(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1} sx={{ fontSize: '0.9rem' }}>
                <Typography variant="body2">
                  <strong>When:</strong>{' '}
                  {new Date(selectedMeeting.startTime).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}{' '}
                  {new Date(selectedMeeting.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} –{' '}
                  {new Date(selectedMeeting.endTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
                {selectedMeeting.location && (
                  <Typography variant="body2">
                    <strong>Location:</strong>{' '}
                    {/^https?:\/\//i.test(selectedMeeting.location) ? (
                      <Box
                        component="span"
                        sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline', wordBreak: 'break-all' }}
                        onClick={async () => {
                          if (await dialog.confirm({ title: 'Open link', message: `Open location in a new tab?\n\n${selectedMeeting.location}`, confirmText: 'Open' })) {
                            window.open(selectedMeeting.location, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      >
                        {selectedMeeting.location}
                      </Box>
                    ) : (
                      selectedMeeting.location
                    )}
                  </Typography>
                )}
                {selectedMeeting.description && (
                  <Typography variant="body2"><strong>Description:</strong> {selectedMeeting.description}</Typography>
                )}
                {selectedMeeting.leadName && (
                  <Typography variant="body2">
                    <strong>Lead:</strong>{' '}
                    <Box
                      component="span"
                      sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => { setSelectedMeeting(null); navigate(`/leads/${selectedMeeting.leadId}`); }}
                    >
                      {selectedMeeting.leadName}
                    </Box>
                  </Typography>
                )}
                {selectedMeeting.customerName && (
                  <Typography variant="body2"><strong>Customer:</strong> {selectedMeeting.customerName}</Typography>
                )}
                {selectedMeeting.leadContactName && (
                  <Typography variant="body2"><strong>Contact:</strong> {selectedMeeting.leadContactName}</Typography>
                )}
                {selectedMeeting.attendees?.length > 0 && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Attendees:</strong></Typography>
                    <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }} useFlexGap>
                      {selectedMeeting.attendees.map(a => (
                        <Chip key={a.id} label={a.fullName} size="small" color="primary" variant="outlined" />
                      ))}
                    </Stack>
                  </Box>
                )}
                {selectedMeeting.notes && (
                  <Typography variant="body2"><strong>Notes:</strong> {selectedMeeting.notes}</Typography>
                )}
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Created by: {selectedMeeting.createdByName}
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                component="a"
                href={generateIcsUrl(selectedMeeting)}
                download={`meeting-${selectedMeeting.id}.ics`}
              >
                Download .ics
              </Button>
              {new Date(selectedMeeting.startTime) >= new Date() && (
                <Button size="small" variant="outlined" color="warning" onClick={() => handleCancel(selectedMeeting.id)}>
                  Cancel Meeting
                </Button>
              )}
              <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(selectedMeeting.id)}>
                Delete
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <BlockerDialog
        open={blockerDialog.open}
        onClose={() => setBlockerDialog({ ...blockerDialog, open: false })}
        onSaved={fetchAll}
        mode={blockerDialog.mode}
        occurrence={blockerDialog.occurrence}
        blockerData={blockerDialog.blockerData}
      />
    </Box>
  );
}
